import { UUID } from '@/types/types';
import { ProjectRenderer } from '../ProjectRenderer';
import { Vector2, Vector2Tuple } from 'three';
import { ThreeEvent } from '../types';
import { SerializedMeasurement, Measurement } from '../objects/Measurement';
import { RenderUtils } from '../utils/RenderUtils';

export type SerializedMeasurementManager = {
	measurements: SerializedMeasurement[];
};

export class MeasurementManager {
	temporaryMeaurement: Measurement | null = null;
	measurements: Map<UUID, Measurement> = new Map();

	constructor(private renderer: ProjectRenderer) {}

	setAvailability() {
		if (this.temporaryMeaurement) {
			this.temporaryMeaurement.dispose();
			this.temporaryMeaurement = null;
		}
	}

	handleMouseMove(pos: Vector2Tuple) {
		if (!this.temporaryMeaurement) return;

		this.temporaryMeaurement.end = new Vector2(...pos);
	}

	handleMouseDown(pos: Vector2Tuple) {
		if (this.temporaryMeaurement) {
			this.temporaryMeaurement.isTemporary = false;
			this.measurements.set(
				this.temporaryMeaurement.id,
				this.temporaryMeaurement
			);

			this.temporaryMeaurement = null;

			this.renderer.emitter.emit(
				ThreeEvent.SET_MEASUREMENTS,
				[...this.measurements.values()].map((measurement, idx) => ({
					id: measurement.id,
					name: `Linia ${idx + 1} (${RenderUtils.roundToPrecision(
						measurement.length,
						2
					)} m)`,
				}))
			);
		} else {
			const newMeasurement = new Measurement(this.renderer, pos, pos);
			this.temporaryMeaurement = newMeasurement;
		}
	}

	handleHoverMeasurement(id: UUID, hovered: boolean) {
		const measurement = this.measurements.get(id);
		if (!measurement) return;

		measurement.hovered = hovered;
	}

	handleRemoveMeasure(id: UUID) {
		const measurement = this.measurements.get(id);
		if (!measurement) return;

		measurement.dispose();
		this.measurements.delete(id);
	}

	export(): SerializedMeasurementManager {
		return {
			measurements: Array.from(this.measurements.values()).map((measurement) =>
				measurement.export()
			),
		};
	}

	loadFromData(data: SerializedMeasurementManager) {
		for (const config of data.measurements) {
			const newMeasurement = new Measurement(
				this.renderer,
				config.start,
				config.end
			);
			newMeasurement.isTemporary = false;
			this.measurements.set(newMeasurement.id, newMeasurement);
		}
	}

	dispose() {
		this.measurements.forEach((measurement) => {
			measurement.dispose();
		});
		this.measurements.clear();

		this.renderer.emitter.emit(ThreeEvent.SET_MEASUREMENTS, []);
	}
}
